import * as React from 'react';

import { Res, CustomerOfferDTO, OfferInListDTO, CustomerForEditionDTO } from 'core/api';
import { CustomerUtils } from 'core/utils';
import { VStack } from '@chakra-ui/react';
import { Modal } from 'core/ui-next/modal';
import { Header, PosScreen, Content, PhoneOnly, BackActionButton, Footer } from 'pos/ui';
import { z } from 'zod';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { BasicButton } from 'core/ui-next';
import { zInt } from 'core/infrastructure-next';


type Props = {
	availableOffers: OfferInListDTO[];
	offer: CustomerOfferDTO;
} & Modal.Actions<CustomerOfferDTO>


const formSchema = z.object({
	Offer: z.custom<OfferInListDTO>(offer => offer !== null && offer !== undefined, { message: Res.Msg.MSG_MUST_SELECT_A_VALUE() }),
	MaxApplicationsPerBusinessDay: z.preprocess(
		value => value === null || value === undefined || (typeof value === 'string' && value.trim() === '') ? '' : value,
		z.union(
			[
				z.literal('', { errorMap: () => ({ message: Res.Msg.MSG_INVALID_FORMAT() }) }),
				zInt({
					nonNegative: true,
					messageInvalid: Res.Msg.MSG_INVALID_FORMAT(),
					messageNonNegative: Res.Msg.MSG_VALUE_MUST_BE_POSITIVE()
				})
			],
			{ errorMap: () => ({ message: Res.Msg.MSG_INVALID_FORMAT() }) }))
});

type FormState = z.infer<typeof formSchema>;


// Opción informativa que se muestra cuando el cliente ya tiene asignadas todas las promociones y no queda ninguna
// disponible. El disabled no es del DTO: es la marca con la que la colección del Select pinta la opción sin dejar
// seleccionarla.
const notAvailableOffersOption = (): OfferInListDTO =>
	({ Name: Res.Web.EDIT_CUSTOMER_OFFERS_NOT_AVAILABLE_OFFERS_LBL(), disabled: true } as unknown as OfferInListDTO);


function toFormState(offer: CustomerOfferDTO): FormState {
	return {
		Offer: offer.Offer,
		MaxApplicationsPerBusinessDay: offer.MaxApplicationsPerBusinessDay ?? ''
	};
}

function toCustomerOffer(offer: CustomerOfferDTO, state: FormState): CustomerOfferDTO {
	return {
		...offer,
		Offer: state.Offer,
		MaxApplicationsPerBusinessDay: state.MaxApplicationsPerBusinessDay === '' ? null : state.MaxApplicationsPerBusinessDay
	};
}


function EditCustomerOfferDialog(props: Props) {

	const { availableOffers, offer, cancel, close } = props;

	const { Form, IntInput, Select } = useControlsFor<FormState>();
	const formRef = React.useRef<FormRef<FormState>>(null);

	// El estado inicial se calcula una sola vez a partir de la promoción que nos llega, mapeando el DTO al esquema del
	// formulario
	const initialState = React.useMemo(() => toFormState(offer), [offer]);

	const offerOptions = React.useMemo(
		() => availableOffers.length === 0 ? [notAvailableOffersOption()] : availableOffers,
		[availableOffers]);


	const onAccept = React.useCallback(async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		close(toCustomerOffer(offer, result.value));
	}, [offer, close]);

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId={'EDIT_CUSTOMER_OFFERS_DIALOG'} size={'small'} useKeyPad={false} >
		<Header title={Res.Web.EDIT_CUSTOMER_OFFERS_DIALOG_TITLE()} onClose={cancel} />
		<Content>
			<VStack width={'full'} gap={'6'} paddingX={'2'} paddingY={'6'}>
				<VStack width={'full'}>
					<Form ref={formRef} initialValue={initialState} schema={formSchema} style={{ width: '100%' }}>
						<VStack width={'full'} gap={'3'}>
							<Select
								linkTo={'Offer'}
								label={Res.Web.EDIT_CUSTOMER_OFFERS_OFFER_LBL()}
								placeholder={Res.Web.EDIT_CUSTOMER_OFFERS_OFFER_LBL()}
								options={offerOptions}
								valueGetter={option => option?.Name ?? ''}
								renderOption={option => option?.Name ?? ''}
								required />
							<IntInput
								linkTo={'MaxApplicationsPerBusinessDay'}
								label={Res.Web.EDIT_CUSTOMER_OFFERS_MAX_APPLICATIONS_LBL()}
								placeholder={Res.Web.EDIT_CUSTOMER_OFFERS_MAX_APPLICATIONS_PLACEHOLDER()}
								allowNegative={false}
							/>
						</VStack>
					</Form>
				</VStack>
				<VStack width={'full'} justifyContent={'center'}>
					<BasicButton type={'success'} width={'full'} text={Res.Common.OK_BTN()} onClick={onAccept} />
				</VStack>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}


export default function showEditCustomerOfferDialog(offer: CustomerOfferDTO, customer: CustomerForEditionDTO): Promise<Result<CustomerOfferDTO>> {
	const availableOffers = CustomerUtils.getAvailableOffers(offer, customer);
	return Modal.show(EditCustomerOfferDialog, { offer, availableOffers });
}
